import React from 'react';
import { connect } from 'react-redux';
import { AfterEffectsClipTemplateViewModel } from 'stageclip-common';

import { baseMapStateToProps } from '../../componentBase';
import { SimpleModalId } from '../../../constants/modalId';
import { callApi } from '../../../helpers/callApi/callApi';
import * as NotificationHelper from '../../../helpers/notificationHelper';
import {
  deleteAfterEffectsClipTemplate,
  downloadAfterEffectsClipTemplate,
  updateAfterEffectsClipTemplate,
  uploadAfterEffectsClipTemplate,
} from '../../../api/clipGenV2/afterEffectsTemplates.actions';
import { ClippingPlatformState } from '../../../store/clippingPlatformState';
import { createModalCloseAction } from '../../../actions/modalActions';

import { TemplatesTable } from './templatesTable';
import { TemplateUpdateFormContainer } from './forms/templateUpdateForm';
import { TemplatesManagementPanelProps, TemplatesManagementPanelState } from './template.types';
import { TemplateUpdateFormDataProps, TemplateUploadFormDataProps } from './forms/templateForm.types';
import { TemplateUploadFormContainer } from './forms/templateUploadForm';

class TemplateManagement extends React.Component<TemplatesManagementPanelProps, TemplatesManagementPanelState> {
  state: TemplatesManagementPanelState = {
    isUploadingTemplate: false,
    isUpdateTemplateModalOpen: false,
    isUpdatingTemplate: false,
    templateToUpdate: null,
  };

  onTemplateUpload = (values: TemplateUploadFormDataProps) => {
    const { fetchTemplates, closeTemplateUploadModal, uploadAfterEffectsTemplate } = this.props;
    callApi({
      action: () => {
        this.setState(prev => ({ ...prev, isUploadingTemplate: true }));

        return uploadAfterEffectsTemplate(values);
      },
      onSuccess: () => {
        this.setState(prev => ({ ...prev, isUploadingTemplate: false }));
        closeTemplateUploadModal();
        NotificationHelper.success('After Effects template uploaded!');
        fetchTemplates();
      },
      onFailure: ({
        payload: {
          response: {
            errors: { domain },
          },
        },
      }) => {
        if (domain) {
          NotificationHelper.error(domain);
        }
      },
    });
  };

  onTemplateDelete = (templateId: string) => {
    const { deleteAfterEffectsTemplate, fetchTemplates } = this.props;
    callApi({
      action: () => deleteAfterEffectsTemplate(templateId),
      onSuccess: () => {
        fetchTemplates();
        NotificationHelper.success('Template deleted!');
      },
      onFailure: () => NotificationHelper.error('Failed to delete template!'),
    });
  };

  onTemplateDownload = (templateId: string) => {
    const { downloadAfterEffectsTemplate } = this.props;
    callApi({
      action: () => downloadAfterEffectsTemplate(templateId),
      onSuccess: () => {
        NotificationHelper.success('Template downloaded!');
      },
      onFailure: () => NotificationHelper.error('Failed to download template!'),
    });
  };

  onTemplateUpdate = (template: AfterEffectsClipTemplateViewModel, values: TemplateUpdateFormDataProps) => {
    const { updateAfterEffectsTemplate, fetchTemplates } = this.props;
    callApi({
      action: () => {
        this.setState(prev => ({ ...prev, isUpdatingTemplate: true }));

        return updateAfterEffectsTemplate(template.id, values);
      },
      onSuccess: () => {
        this.setState(prev => ({ ...prev, isUpdateTemplateModalOpen: false, isUpdatingTemplate: false }));
        fetchTemplates();
        NotificationHelper.success('Template updated!');
      },
      onFailure: () => NotificationHelper.error('Failed to update template!'),
    });
  };

  handleUpdateModalOpen = async (template: AfterEffectsClipTemplateViewModel) => {
    await this.setState(prev => ({ ...prev, isUpdateTemplateModalOpen: true, templateToUpdate: template }));
  };

  handleUpdateModalClose = () => {
    this.setState(prev => ({ ...prev, isUpdateTemplateModalOpen: false, templateToUpdate: null }));
  };

  render = () => {
    const { isUploadingTemplate, isUpdateTemplateModalOpen, isUpdatingTemplate, templateToUpdate } = this.state;
    const { templates, getTemplatesLoading, getTemplatesError } = this.props;

    if (getTemplatesError) {
      return <div>Something went wrong</div>;
    }

    return (
      <>
        <TemplateUploadFormContainer onUpload={this.onTemplateUpload} isLoading={isUploadingTemplate} />
        <TemplatesTable
          templates={templates}
          onClickDelete={this.onTemplateDelete}
          onClickDownload={this.onTemplateDownload}
          onClickUpdate={this.handleUpdateModalOpen}
          getTemplatesLoading={getTemplatesLoading}
        />
        {isUpdateTemplateModalOpen && (
          <TemplateUpdateFormContainer
            form={SimpleModalId.UpdateTemplateModal}
            isOpen={isUpdateTemplateModalOpen}
            isLoading={isUpdatingTemplate}
            onClose={this.handleUpdateModalClose}
            onUpdate={this.onTemplateUpdate}
            template={templateToUpdate}
          />
        )}
      </>
    );
  };
}

const mapDispatchToProps = (dispatch: Function) => ({
  uploadAfterEffectsTemplate: (body: TemplateUploadFormDataProps) => dispatch(uploadAfterEffectsClipTemplate(body)),
  updateAfterEffectsTemplate: (templateId: string, body: TemplateUpdateFormDataProps) =>
    dispatch(updateAfterEffectsClipTemplate(templateId, body)),
  deleteAfterEffectsTemplate: (templateId: string) => dispatch(deleteAfterEffectsClipTemplate(templateId)),
  downloadAfterEffectsTemplate: (templateId: string) => dispatch(downloadAfterEffectsClipTemplate(templateId)),
  closeTemplateUploadModal: () => dispatch(createModalCloseAction(SimpleModalId.AddTemplateModal)),
});

const mapStateToProps = (state: ClippingPlatformState) => baseMapStateToProps(state);

export const TemplateManagementContainer = connect(mapStateToProps, mapDispatchToProps)(TemplateManagement);
